import { forwardRef, Fragment, useEffect, useImperativeHandle } from 'react';

import { QuestionSelectStep } from 'pages/custom_tests_v1/components/custom_test_create_wizard/steps/QuestionSelectStep';
import type { QuestionTree, QuestionTreeResult } from 'pages/custom_tests_v1/components/question_picker/QuestionPicker';
import { ProductSelectStep } from 'pages/custom_tests_v1/components/custom_test_create_wizard/steps/ProductSelectStep';
import { CustomTestCreateWizardContext } from 'pages/custom_tests_v1/components/custom_test_create_wizard/context';
import type { CustomTestCategory, CustomTestLanguage, CustomTestProduct } from 'pages/custom_tests_v1/types';
import { ConfirmStep } from 'pages/custom_tests_v1/components/custom_test_create_wizard/steps/ConfirmStep';
import { DetailsStep } from 'pages/custom_tests_v1/components/custom_test_create_wizard/steps/DetailsStep';
import { GMetrixStepper } from 'components/GMetrixStepper/GMetrixStepper';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { useNotification } from 'app/notifications';
import { CustomTestApi } from 'apis/CustomTestApi';
import { LanguagesApi } from 'apis/LanguagesApi';
import { useSuperState } from 'hooks';
import { useIntl } from 'react-intl';
import { useApp } from 'app';

enum EStep {
    DETAILS,
    PRODUCTS,
    QUESTIONS,
    CONFIRM
}

enum EMode {
    TRAINING = 1,
    TESTING
}

// TODO(custom-test-magic-ids): hardcoded product/category id lists carried over from the legacy
// ASPX custom-test page. Move to DB-driven metadata (a "supports multi-app" flag per product and a
// category -> delivery/testType mapping) so catalog changes don't require code edits. Mirror lists
// live in ProductSelectStep, QuestionsTab (frontend) and CustomTestController (backend).

// Hybrid MOS categories that expose the In-App/In-Browser delivery choice.
const HYBRID_DELIVERY_CATEGORIES = [21, 40];

// Products that cannot be combined with others into a multi-application test (Outlook, OneNote,
// Office 365, SharePoint, etc.). Selecting more than one product where any is in this list is rejected.
const MULTI_APP_EXCLUDED_PRODUCTS = [5, 20, 31, 36, 37, 45, 49, 50, 103, 107, 108];

export type CustomTestCreateWizardHandle = {
    hide: () => void;
    show: () => void;
    visible: boolean;
};

type CustomTestCreateWizardProps = {
    cloneTestId?: number;
    onClose?: () => void;
    onSuccess: (testId: number) => void;
};

export type CustomTestCreateWizardContextValue = {
    name: string;
    categories: CustomTestCategory[];
    categoryId: number;
    deliveryType: number;
    fetchQuestions: () => Promise<void>;
    handleDeliveryChange: (deliveryType: number) => void;
    handleQuestionsChange: (questionIds: string, totalSelected: number) => void;
    handleToggleCategory: (categoryId: number) => void;
    handleToggleProduct: (productId: number) => void;
    isEditMode?: boolean;
    languageId?: number;
    languages?: { Id: number; Name: string }[];
    loadingProducts: boolean;
    loadingQuestions: boolean;
    minScore: number;
    minutes: number;
    modeId: EMode;
    nameValidationError?: string;
    productIds: number[];
    products: CustomTestProduct[];
    questionTree: QuestionTree;
    randomly: number;
    selectedQuestions: number;
    setProviderState: (state: any) => void;
    totalQuestions?: number;
    useRandomly: boolean;
};

export const CustomTestCreateWizard = forwardRef<CustomTestCreateWizardHandle, CustomTestCreateWizardProps>((props, forwardedRef) => {

    const { cloneTestId, onClose, onSuccess } = props;

    const [state, setState, resetState] = useSuperState({
        name: '',
        categories: [] as CustomTestCategory[],
        categoryId: 0,
        deliveryType: 3,
        languageId: 0,
        languages: [] as CustomTestLanguage[],
        loadingProducts: false,
        loadingQuestions: false,
        minScore: 70,
        minutes: 60,
        modeId: EMode.TESTING as EMode,
        nameValidationError: '',
        productIds: [] as number[],
        products: [] as CustomTestProduct[],
        questionIds: '',
        questionsXml: '',
        questionTree: { Products: [] } as QuestionTree,
        randomly: 0,
        selectedQuestions: 0,
        step: EStep.DETAILS as EStep,
        submitting: false,
        useRandomly: false,
        validating: false,
        validationError: '',
        visible: false
    });

    const { name, categories, categoryId, deliveryType, languageId, languages, loadingProducts, loadingQuestions, minScore,
        minutes, modeId, productIds, products, questionTree, randomly, selectedQuestions, step, submitting, useRandomly,
        validating, visible } = state;

    const { selectedPartner } = useApp();
    const { formatMessage } = useIntl();
    const { createToast } = useNotification();

    const show = () => setState({ visible: true });

    const hide = () => setState({ visible: false });

    const handleToggleCategory = (categoryId: number) => {

        if (categoryId === state.categoryId) return;

        // Hybrid MOS categories default to the In-App pool (matching the legacy page default);
        // all other categories are In-Browser (3).
        setState({
            categoryId,
            deliveryType: HYBRID_DELIVERY_CATEGORIES.includes(categoryId) ? 1 : 3,
            productIds: [],
            products: []
        });

    };

    // Switching the In-App/In-Browser pool invalidates the current question selection,
    // since the two pools contain different questions.
    const handleDeliveryChange = (deliveryType: number) => {

        if (deliveryType === state.deliveryType) return;

        setState({
            deliveryType,
            questionIds: '',
            questionsXml: '',
            questionTree: { Products: [] } as QuestionTree,
            selectedQuestions: 0
        });

    };

    const handleClose = () => {

        resetState();
        onClose?.();

    };

    const validateDetailsStep = async () => {

        if (!state.name.trim()) {

            setState({ nameValidationError: formatMessage({ id: 'test_name_is_required.88a8be5d', defaultMessage: 'Test name is required.' }), validationError: '' });
            return false;

        }

        if (state.minScore < 1 || state.minScore > 100) {

            setState({ nameValidationError: '', validationError: formatMessage({ id: 'minimum_score_must_be_between_1_and_100.dbb31ccd', defaultMessage: 'Minimum score must be between 1 and 100.' }) });
            return false;

        }

        if (state.minutes < 1 || state.minutes > 600) {

            setState({ nameValidationError: '', validationError: formatMessage({ id: 'time_limit_must_be_between_1_and_600_min.e27649b8', defaultMessage: 'Time limit must be between 1 and 600 minutes.' }) });
            return false;

        }

        try {

            setState({ nameValidationError: '', validating: true, validationError: '' });

            const isUnique = await CustomTestApi.ValidateTestName(state.name.trim(), selectedPartner.id);

            if (!isUnique) {

                setState({ nameValidationError: formatMessage({ id: 'test_name_already_in_use.d76e3541', defaultMessage: 'Test Name already in use.' }), validating: false });
                return false;

            }

        } catch {

            setState({ nameValidationError: '', validating: false, validationError: formatMessage({ id: 'unable_to_validate_test_name_please_try.de4a12c1', defaultMessage: 'Unable to validate test name. Please try again.' }) });
            return false;

        }

        setState({ nameValidationError: '', validating: false, validationError: '' });
        return true;

    };

    const validateProductsStep = () => {

        if (state.productIds.length === 0) {

            setState({ validationError: formatMessage({ id: 'please_select_at_least_one_product.2158e646', defaultMessage: 'Please select at least one product.' }) });
            return false;

        }

        // Outlook/OneNote/Office 365/SharePoint questions can't be combined with other apps in a
        // single multi-application test.
        if (state.productIds.length > 1 && state.productIds.some(id => MULTI_APP_EXCLUDED_PRODUCTS.includes(id))) {

            setState({ validationError: formatMessage({ id: 'due_to_technical_differences_between_the.c1171309', defaultMessage: 'Due to technical differences between the Outlook, OneNote, Office 365, and Sharepoint exams and the other applications in Microsoft Office, questions from these four exams cannot be part of a multiple application test.' }) });
            return false;

        }

        setState({ validationError: '' });
        return true;

    };

    const validateQuestionsStep = () => {

        if (state.selectedQuestions === 0) {

            setState({ validationError: formatMessage({ id: 'please_select_at_least_one_question.c98fa9f5', defaultMessage: 'Please select at least one question.' }) });
            return false;

        }

        if (state.useRandomly && (state.randomly < 1 || state.randomly > state.selectedQuestions)) {

            setState({ validationError: formatMessage({ id: 'random_question_count_must_be_between_1.b044613b', defaultMessage: 'Random question count must be between 1 and the total selected questions.' }) });
            return false;

        }

        setState({ validationError: '' });
        return true;

    };

    const validateStep = async () => {

        switch (state.step){

            case EStep.DETAILS:
                return await validateDetailsStep();
            case EStep.PRODUCTS:
                return validateProductsStep();
            case EStep.QUESTIONS:
                return validateQuestionsStep();
            default:
                return true;

        }

    };

    const handleNext = async () => {

        if (!await validateStep()) return;

        const nextSteps = {
            [EStep.DETAILS]: EStep.PRODUCTS, // 0 -> 1
            [EStep.PRODUCTS]: EStep.QUESTIONS, // 1 -> 2
            [EStep.QUESTIONS]: EStep.CONFIRM // 2 -> 3
        };

        setState({
            step: nextSteps[state.step]
        });

    };

    const handleBack = () => {

        const prevSteps = {
            [EStep.CONFIRM]: EStep.QUESTIONS, // 3 -> 2
            [EStep.PRODUCTS]: EStep.DETAILS, // 1 -> 0
            [EStep.QUESTIONS]: EStep.PRODUCTS // 2 -> 1
        };

        setState({
            step: prevSteps[state.step],
            validationError: ''
        });

    };

    const handleSubmit = async () => {

        try {

            setState({ submitting: true });

            const response = await CustomTestApi.Create({
                categoryId: state.categoryId,
                languageId: state.languageId,
                minScore: state.minScore,
                minutes: state.minutes,
                modeId: state.modeId,
                multiProductTest: state.productIds.length > 1,
                productId: state.productIds[0] ?? 0,
                questionIds: state.questionIds,
                questionsXml: state.questionsXml,
                randomly: state.useRandomly ? state.randomly : 0,
                testName: state.name,
                totalQuestions: state.selectedQuestions,
                useRandomly: state.useRandomly
            });

            if (response?.Done) {

                createToast(
                    cloneTestId
                        ? (formatMessage({ id: 'test_cloned_successfully.68bae141', defaultMessage: 'Test cloned successfully.' }))
                        : (formatMessage({ id: 'custom_test_created_successfully.9197a4d4', defaultMessage: 'Custom test created successfully.' })),
                    'success'
                );

                setTimeout(() => {

                    resetState();

                    // WPL3_CreateCustomTest returns the new test id in @msg.
                    onSuccess(parseInt(response.Message ?? '', 10) || 0);

                }, 1500);

            } else {

                createToast(
                    response?.Message ?? (formatMessage({ id: 'failed_to_create_custom_test.ce47f167', defaultMessage: 'Failed to create custom test.' })),
                    'danger'
                );
                setState({ submitting: false });

            }

        } catch (e: any) {

            createToast(
                e?.message ?? (formatMessage({ id: 'an_error_occurred_while_creating_the_tes.a478f77f', defaultMessage: 'An error occurred while creating the test.' })),
                'danger'
            );
            setState({ submitting: false });

        }

    };

    const handleToggleProduct = (productId: number) => {

        const updated = state.productIds.includes(productId)
            ? state.productIds.filter(id => id !== productId)
            : [...state.productIds, productId];

        setState({ productIds: updated });

    };

    const handleQuestionsChange = (questionIds: string, totalSelected: number) => {

        setState({ questionIds, selectedQuestions: totalSelected });

    };

    const getActionButtonText = (): string => {

        if (state.step === EStep.CONFIRM) {

            if (state.submitting) {

                return formatMessage({ id: 'creating.2155605d', defaultMessage: 'Creating...' });

            }

            return cloneTestId
                ? (formatMessage({ id: 'clone_test.50da8012', defaultMessage: 'Clone Test' }))
                : (formatMessage({ id: 'create_test.c699ee0a', defaultMessage: 'Create Test' }));

        }

        if (state.validating) {

            return formatMessage({ id: 'validating.e1043608', defaultMessage: 'Validating...' });

        }

        return formatMessage({ id: 'next.50b7834e', defaultMessage: 'Next' });

    };

    const handleActionClick = async () => {

        if (state.step === EStep.CONFIRM) {

            handleSubmit();

        } else {

            await handleNext();

        }

    };

    const fetchProducts = async () => {

        try {

            setState({ loadingProducts: true });

            const products = await CustomTestApi.GetProductsByCategory(state.categoryId, selectedPartner.id);
            setState({ loadingProducts: false, products });

        } catch (e){

            console.error(e);
            setState({ loadingProducts: false });

        }

    };

    const fetchQuestions = async () => {

        try {

            setState({ loadingQuestions: true });

            const productIdsStr = state.productIds.join(',');
            const result: QuestionTreeResult = await CustomTestApi.GetQuestions(
                productIdsStr,
                state.categoryId,
                state.languageId,
                state.deliveryType
            );

            setState({
                loadingQuestions: false,
                questionsXml: result.XmlString,
                questionTree: result
            });

        } catch (e) {

            console.error(e);
            setState({ loadingQuestions: false });

        }

    };

    const fetchInitialData = async () => {

        try {

            const [categories, languages] = await Promise.all([
                CustomTestApi.GetCategories(selectedPartner.id),
                LanguagesApi.GetAll()
            ]);

            const typedLanguages = languages as CustomTestLanguage[];

            setState({
                categories: categories as CustomTestCategory[],
                languageId: state.languageId || typedLanguages[0]?.Id || 0,
                languages: typedLanguages
            });

        } catch {

            // Silently handle — categories/languages may be loaded by parent
        }

    };

    // Fetch categories and languages when the wizard is launched
    useEffect(() => {

        if (!state?.visible) return;

        fetchInitialData();

    }, [state?.visible]);

    // Refresh products when category changes
    useEffect(() => {

        if (!state?.categoryId) return;
        fetchProducts();

    }, [state?.categoryId]);

    useImperativeHandle(forwardedRef, (): CustomTestCreateWizardHandle => ({
        hide,
        show,
        visible
    }));

    const stepItems = [
        { label: formatMessage({ id: 'details.7799b923', defaultMessage: 'Details' }) },
        { label: formatMessage({ id: 'products.b940f0f9', defaultMessage: 'Products' }) },
        { label: formatMessage({ id: 'questions.940c8cce', defaultMessage: 'Questions' }) },
        { label: formatMessage({ id: 'confirm.297ab8f4', defaultMessage: 'Confirm' }) }
    ];

    const context: CustomTestCreateWizardContextValue = {
        name,
        categories,
        categoryId,
        deliveryType,
        fetchQuestions,
        handleDeliveryChange,
        handleQuestionsChange,
        handleToggleCategory,
        handleToggleProduct,
        languageId,
        languages,
        loadingProducts,
        loadingQuestions,
        minScore,
        minutes,
        modeId,
        nameValidationError: state.nameValidationError,
        productIds,
        products,
        questionTree,
        randomly,
        selectedQuestions,
        setProviderState: setState,
        useRandomly
    };

    const Steps = [DetailsStep, ProductSelectStep, QuestionSelectStep, ConfirmStep];
    const StepComponent = Steps[step] || Fragment;

    return (
        <CustomTestCreateWizardContext.Provider value={context}>
            <GMetrixDialog
                dialogLabel={cloneTestId
                    ? (formatMessage({ id: 'clone_test.50da8012', defaultMessage: 'Clone Test' }))
                    : (formatMessage({ id: 'create_custom_test.f47f2b9d', defaultMessage: 'Create Custom Test' }))
                }
                actionButtonClick={handleActionClick}
                actionButtonDisabled={submitting || validating}
                actionButtonText={getActionButtonText()}
                closeButtonText={step === EStep.DETAILS ? (formatMessage({ id: 'cancel.89a86adc', defaultMessage: 'Cancel' })) : (formatMessage({ id: 'back.b6238c9e', defaultMessage: 'Back' }))}
                onCancel={step === EStep.DETAILS ? handleClose : handleBack}
                onClose={handleClose}
                show={visible}
                size="xl"
            >
                <div className="my-4">
                    <GMetrixStepper
                        handleChange={e => {

                            if (e.value <= state.step) {

                                setState({ step: e.value, validationError: '' });

                            }

                        }}
                        linear={false}
                        setStep={newStep => setState({ step: newStep as EStep })}
                        step={state.step}
                        stepItems={stepItems}
                    />
                </div>

                {state.validationError && (
                <div className="alert alert-danger">
                    {state.validationError}
                </div>
                )}

                <StepComponent />

            </GMetrixDialog>
        </CustomTestCreateWizardContext.Provider>
    );

});

CustomTestCreateWizard.displayName = 'CustomTestCreateWizard';
